import { ProcessingSpinner } from "@storytell/chat-ui/ui";
import { Logo } from "@storytell/core";
import { type LiveSummaryItemData, LiveSummaryView } from "@storytell/ui/live-summary";
import {
  TbArrowUpRight,
  TbCheck,
  TbChevronDown,
  TbCircle,
  TbCopy,
  TbHighlight,
  TbLock,
  TbMessage2,
  TbNote,
  TbPencil,
  TbPointer,
  TbSparkles,
  TbSquare,
  TbTypography,
  TbUser,
  TbWaveSquare,
  TbX,
} from "solid-icons/tb";
import { type Component, For, type JSX, type ParentComponent, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { type Scene, useScene, useTyping } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import { ScaledFrame } from "../ScaledFrame";
import {
  Caret,
  OpenDocument,
  PeopleStack,
  SessionPill,
  SessionWindow,
  startedLabel,
  Swap,
  type TranscriptEntry,
} from "./chief";
import { NotesLine } from "./notes";
import { at, Cursor, MockScene, type Person } from "./parts";

export type CollabData = {
  project: string;
  session: string;
  started: string;
  clock: string;
  host: Person;
  others: Person[];
  /** Joins the session at the start of the canvas scene. */
  joiner: Person;
  /** Draws on the canvas at the same time as the joiner. */
  sketcher: Person;
  more: number;
  note: string;
  /** Each line types in at once with the others, by its own typist; the first one's typist mentions you. */
  doc: { title: string; body: string; typing: { text: string; typist: Person }[] };
  link: string;
  shared: { transcript: TranscriptEntry[]; notes: LiveSummaryItemData[] };
};

/** The Live Session window every collaboration scene shares, with who's here on its bar. */
const CollabWindow: ParentComponent<{
  data: CollabData;
  tab: "notes" | "documents";
  joinAt?: number;
  invite?: JSX.Element;
  /** Laid over the whole window, outside the desktop app's skin, for a web page opened from it. */
  overlay?: JSX.Element;
}> = (props) => {
  const { t, currentLanguage } = useI18n();
  return (
    <ScaledFrame chrome="app" width={960} height={560} narrow={{ width: 400, height: 760 }}>
      <SessionWindow
        project={props.data.project}
        title={props.data.session}
        facts={`${t("marketing.mock.live")} · ${startedLabel(props.data.started, currentLanguage())}`}
        live
        tab={props.tab}
        bar={
          <SessionPill
            clock={props.data.clock}
            invite={props.invite}
            people={
              <PeopleStack
                people={[props.data.host, ...props.data.others, props.data.joiner]}
                host={props.data.host}
                more={props.data.more}
                joinAt={props.joinAt}
              />
            }
          />
        }
      >
        {props.children}
      </SessionWindow>
      {props.overlay}
    </ScaledFrame>
  );
};

/** Select, the two draw tools, what a drag creates, commenting, then Chief. */
const TOOL_GROUPS = [
  [TbPointer],
  [TbPencil, TbHighlight],
  [TbTypography, TbNote, TbSquare, TbCircle, TbArrowUpRight],
  [TbMessage2],
];

/** A collaborator's cursor on the board: their name on their colour. */
const RemoteLabel: Component<{ person: Person }> = (props) => (
  <span
    class="rounded px-1 text-[10px] font-semibold whitespace-nowrap text-white"
    style={{ "background-color": props.person.color }}
  >
    {props.person.name}
  </span>
);

export const CollabCanvas: Component<{ data: CollabData; label: string }> = (props) => {
  const { t } = useI18n();
  const scene = useScene();
  const sam = () => props.data.joiner;
  const priya = () => props.data.sketcher;
  return (
    <MockScene scene={scene} label={props.label}>
      <CollabWindow data={props.data} tab="documents" joinAt={0.2}>
        <OpenDocument>
          {/* Board pixels: each stroke's points are the collab-pen keyframes for its layout. */}
          <svg aria-hidden="true" class="absolute top-0 left-0 hidden h-110 w-155 @2xl:block" viewBox="0 0 620 440">
            <path
              d="M70 250 C110 180, 190 170, 230 220 S320 290, 370 230"
              pathLength="100"
              fill="none"
              stroke-width="4"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="draw-in"
              style={{ stroke: sam().color, opacity: 0.8, "animation-delay": "0.4s" }}
            />
            <path
              d="M80 380 C130 330, 200 340, 240 380 S320 420, 360 370"
              pathLength="100"
              fill="none"
              stroke-width="4"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="draw-in"
              style={{ stroke: priya().color, opacity: 0.8, "animation-delay": "0.6s" }}
            />
          </svg>
          <svg aria-hidden="true" class="absolute top-0 left-0 h-168 w-89 @2xl:hidden" viewBox="0 0 356 672">
            <path
              d="M60 200 C92 140, 156 130, 188 170 S260 240, 300 190"
              pathLength="100"
              fill="none"
              stroke-width="4"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="draw-in"
              style={{ stroke: sam().color, opacity: 0.8, "animation-delay": "0.4s" }}
            />
            <path
              d="M60 600 C96 560, 150 570, 182 600 S260 630, 300 590"
              pathLength="100"
              fill="none"
              stroke-width="4"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="draw-in"
              style={{ stroke: priya().color, opacity: 0.8, "animation-delay": "0.6s" }}
            />
          </svg>
          <div
            class="card-appear absolute start-42.5 top-7 w-40 rounded-[3px] bg-panel p-3 text-sm text-primary-text shadow-md @2xl:start-100 @2xl:top-10"
            style={{ outline: `2px solid ${sam().color}`, "animation-delay": "2.4s" }}
          >
            {props.data.note}
          </div>
          <div class="absolute inset-s-6 top-75 h-56 w-77 @2xl:start-100 @2xl:top-35 @2xl:h-47.5 @2xl:w-50">
            <div
              class="card-pass absolute inset-0 overflow-hidden rounded-lg border border-brand/50 bg-panel shadow-lg"
              style={at(2.6)}
            >
              <div class="flex flex-col gap-3 p-4">
                <div class="h-4 w-1/4 rounded bg-brand/10" />
                <div class="h-9 w-2/3 rounded bg-brand/10" />
                <div class="h-3 w-1/2 rounded bg-brand/10" />
              </div>
              <div class="absolute inset-x-0 bottom-0 flex items-center gap-2 border-t border-primary-border bg-panel px-3 py-2 text-xs text-primary-text">
                <ProcessingSpinner class="size-3.5 shrink-0 border-brand border-t-transparent" />
                <div class="min-w-0 flex-1">
                  <Swap
                    swapAt={3.6}
                    before={t("marketing.mock.collab.designing")}
                    after={t("marketing.mock.collab.checking")}
                  />
                  <p class="truncate text-[10px] text-muted-text">{props.data.note}</p>
                </div>
              </div>
            </div>
            <span
              class="card-appear absolute inset-s-0 -top-4 block truncate text-[11px] leading-tight text-muted-text"
              style={at(4.7)}
            >
              {t("marketing.mock.collab.web_mockup")}
            </span>
            <div
              class="card-appear absolute inset-0 overflow-hidden rounded-md border border-primary-border bg-white"
              style={at(4.7)}
            >
              <div class="light flex flex-col gap-2 p-3">
                <span class="h-3 w-20 rounded-sm bg-brand/80" />
                <span class="h-2 w-36 rounded-sm bg-primary-text/20" />
                <span class="h-2 w-28 rounded-sm bg-primary-text/20" />
                <span class="mt-1 grid grid-cols-3 gap-1.5">
                  <span class="h-12 rounded-sm bg-primary-text/8" />
                  <span class="h-12 rounded-sm bg-primary-text/8" />
                  <span class="h-12 rounded-sm bg-primary-text/8" />
                </span>
                <span class="h-5 w-20 self-end rounded-md bg-brand" />
              </div>
            </div>
          </div>
          <span class="collab-pen absolute inset-s-0 top-0 z-10" style={at(0.4)}>
            <RemoteLabel person={sam()} />
          </span>
          <span class="collab-pen collab-pen-alt absolute inset-s-0 top-0 z-10" style={at(0.6)}>
            <RemoteLabel person={priya()} />
          </span>
          <div class="absolute inset-s-2 top-1/2 z-20 -translate-y-1/2">
            <div class="flex flex-col items-center gap-1 rounded-lg border border-primary-border bg-panel p-1 text-primary-text shadow-lg">
              <For each={TOOL_GROUPS}>
                {(group, i) => (
                  <>
                    <Show when={i() > 0}>
                      <span class="my-0.5 h-px w-5 bg-primary-border" />
                    </Show>
                    <For each={group}>
                      {(tool) => (
                        <span
                          class="grid size-7 place-items-center rounded-md text-muted-text"
                          classList={{ "bg-panel-hover text-accent": tool === TbPencil }}
                        >
                          <Dynamic component={tool} class="size-4" aria-hidden="true" />
                        </span>
                      )}
                    </For>
                  </>
                )}
              </For>
              <span class="my-0.5 h-px w-5 bg-primary-border" />
              <span
                class="press relative grid size-7 place-items-center rounded-md bg-brand/10 text-brand dark:bg-brand/25 dark:text-white"
                style={at(2.6)}
              >
                <TbSparkles class="size-4" aria-hidden="true" />
                <span
                  class="card-pass absolute inset-s-full top-1/2 z-10 ms-2 -translate-y-1/2 rounded-md bg-primary-text px-2 py-1 text-xs whitespace-nowrap text-inverted-text"
                  style={at(2.2)}
                >
                  {t("marketing.mock.collab.mockup")}
                </span>
              </span>
            </div>
          </div>
        </OpenDocument>
      </CollabWindow>
    </MockScene>
  );
};

export const CollabDocuments: Component<{ data: CollabData; label: string }> = (props) => {
  const { t } = useI18n();
  const scene = useScene();
  const mentioner = () => props.data.doc.typing[0].typist;
  return (
    <MockScene scene={scene} label={props.label}>
      <CollabWindow data={props.data} tab="documents">
        <OpenDocument>
          <div class="h-full pt-6 text-primary-text">
            <div class="mb-4 px-6 @2xl:ps-25 @2xl:pe-8">
              <h1 class="text-2xl leading-tight font-semibold wrap-break-word">{props.data.doc.title}</h1>
            </div>
            <div class="flex flex-col gap-3 px-6 text-sm @2xl:ps-25 @2xl:pe-8">
              <p>{props.data.doc.body}</p>
              <For each={props.data.doc.typing}>
                {(line, i) => (
                  <TypedLine scene={scene} text={line.text} typist={line.typist} delayMs={600 + i() * 400} />
                )}
              </For>
              <div class="msg-in mk-drafting mt-4 flex flex-col gap-2 py-1.5" style={at(1.2)}>
                <span class="mk-drafting-label">{t("marketing.mock.collab.drafting")}</span>
                <span class="h-2.5 w-full rounded-full bg-secondary-text/15" />
                <span class="h-2.5 w-11/12 rounded-full bg-secondary-text/15" />
                <span class="h-2.5 w-2/3 rounded-full bg-secondary-text/15" />
              </div>
            </div>
          </div>
        </OpenDocument>
        <div
          class="card-appear absolute inset-s-4 bottom-16 z-10 flex items-center gap-3 rounded-xl border border-primary-border bg-tile px-3.5 py-2.5 shadow-xl"
          style={at(4.2)}
        >
          <TbMessage2 class="size-4 shrink-0 text-accent" aria-hidden="true" />
          <span class="max-w-72 text-sm text-primary-text">
            {t("marketing.mock.collab.mentioned", { name: mentioner().name, doc: props.data.doc.title })}
          </span>
          <span class="grid size-5 shrink-0 place-items-center rounded-sm text-muted-text">
            <TbX class="size-3.5" aria-hidden="true" />
          </span>
        </div>
      </CollabWindow>
    </MockScene>
  );
};

/** One collaborator's line typing in at their caret. */
const TypedLine: Component<{ scene: Scene; text: string; typist: Person; delayMs: number }> = (props) => {
  const typed = useTyping(() => props.text, props.scene, props.delayMs);
  return (
    <p class="min-h-6">
      {typed()}
      <Caret person={props.typist} />
    </p>
  );
};

export const CollabShare: Component<{ data: CollabData; label: string }> = (props) => {
  const { t } = useI18n();
  const scene = useScene();
  return (
    <MockScene scene={scene} label={props.label}>
      <CollabWindow
        data={props.data}
        tab="notes"
        invite={<Cursor start={0.2} />}
        overlay={<SharedPage data={props.data} />}
      >
        <div class="px-6 pt-5 @2xl:px-10">
          <h1 class="mb-2 truncate text-[1.625rem] font-semibold tracking-[-0.022em]">{props.data.session}</h1>
          <For each={props.data.shared.notes}>{(note) => <NotesLine text={note.text} />}</For>
        </div>
        <div class="absolute inset-x-3 bottom-16 z-10 @2xl:inset-x-auto @2xl:inset-s-6 @2xl:w-85">
          <div
            class="card-appear flex flex-col gap-4 rounded-lg border border-primary-border bg-tile p-4 text-sm text-primary-text shadow-xl"
            style={at(1.3)}
          >
            <div class="flex items-start justify-between gap-2">
              <h2 class="text-base font-semibold text-primary-text">{t("marketing.mock.collab.share_title")}</h2>
              <span class="-me-1 grid size-7 shrink-0 place-items-center rounded-md text-muted-text">
                <TbX class="size-4" aria-hidden="true" />
              </span>
            </div>
            <div class="flex flex-col gap-2">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="text-sm text-secondary-text">{t("marketing.mock.collab.anyone")}</span>
                <span class="flex min-h-8 shrink-0 items-center gap-1.5 rounded-md border border-primary-border bg-panel px-2.5 text-sm font-medium text-primary-text">
                  {t("marketing.mock.collab.collaborator")}
                  <TbChevronDown class="size-3 text-muted-text" aria-hidden="true" />
                </span>
              </div>
              <p class="text-xs leading-relaxed text-muted-text">{t("marketing.mock.collab.collaborator_desc")}</p>
            </div>
            <span class="press relative" style={at(2.3)}>
              <span class="grid">
                <span class="exit col-start-1 row-start-1" style={at(2.5)}>
                  <span class="mac-push mac-push-brand flex min-h-10 w-full items-center justify-center gap-2 px-3 py-2 text-sm font-medium">
                    <TbCopy class="size-4 shrink-0" aria-hidden="true" />
                    {t("marketing.mock.collab.copy")}
                  </span>
                </span>
                <span class="card-appear col-start-1 row-start-1" style={at(2.5)}>
                  <span class="mac-push mac-push-tinted flex min-h-10 w-full items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white [--mac-push-tint:var(--color-success-strong)]">
                    <TbCheck class="size-4 shrink-0" aria-hidden="true" />
                    {t("marketing.mock.collab.copied")}
                  </span>
                </span>
              </span>
              <Cursor start={1.2} />
            </span>
          </div>
        </div>
      </CollabWindow>
    </MockScene>
  );
};

/** The browser window the link opens: the web app's shared live session page. */
const SharedPage: Component<{ data: CollabData }> = (props) => {
  const { t } = useI18n();
  return (
    <div
      class="row-slide absolute inset-x-5.5 top-14.5 bottom-auto z-20 flex h-75 flex-col overflow-hidden rounded-xl border border-primary-border bg-canvas font-sans shadow-2xl @2xl:inset-s-auto @2xl:end-6.5 @2xl:top-17.5 @2xl:h-97.5 @2xl:w-130"
      style={at(3.0)}
    >
      <p class="flex items-center gap-2 border-b border-primary-border bg-panel px-3 py-1.5">
        <span class="flex gap-1">
          <span class="size-2 rounded-full bg-mock-close" />
          <span class="size-2 rounded-full bg-mock-minimize" />
          <span class="size-2 rounded-full bg-mock-zoom" />
        </span>
        <span class="mx-auto flex items-center gap-1 truncate rounded bg-inset px-2 py-0.5 font-mono text-[10px] text-secondary-text">
          <TbLock class="size-2.5" aria-hidden="true" />
          {props.data.link}
        </span>
      </p>
      <div class="flex h-10 shrink-0 items-center gap-2 border-b border-primary-border bg-panel px-3">
        <Logo class="h-4 w-auto" />
        <span class="text-secondary-text">/</span>
        <span class="text-xs text-secondary-text">{t("shared_live_session.shared_view")}</span>
      </div>
      <div class="flex shrink-0 items-center gap-2 border-b border-primary-border bg-panel px-3 py-2">
        <TbWaveSquare class="size-4 shrink-0 text-brand" aria-hidden="true" />
        <p class="truncate text-sm font-semibold text-primary-text">{props.data.session}</p>
        <span class="flex shrink-0 items-center gap-1.5 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-semibold text-red-500">
          <span class="size-1.5 rounded-full bg-red-500 motion-safe:animate-pulse" />
          {t("live_sessions.live_badge")}
        </span>
        <span class="ms-auto flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-2.5 py-1 text-xs text-white">
          <TbWaveSquare class="size-3.5" aria-hidden="true" />
          {t("live_sessions.open_in_chief")}
        </span>
      </div>
      <div class="flex min-h-0 flex-1 gap-px bg-primary-border">
        <section class="hidden min-w-0 flex-col bg-panel @2xl:flex @2xl:w-[42%]">
          <p class="border-b border-primary-border px-3 py-2 text-xs font-semibold text-primary-text">
            {t("live_sessions.transcript")}
          </p>
          <ul class="space-y-3 px-3 pt-3 text-xs">
            <For each={props.data.shared.transcript}>
              {(turn) => (
                <li class="flex gap-2">
                  <span
                    class="w-1 shrink-0 rounded-full"
                    classList={{ "bg-slate-500": turn.local, "bg-sky-500": !turn.local }}
                  />
                  <span class="min-w-0 flex-1">
                    <span class="mb-1 flex items-baseline gap-2">
                      <span
                        class="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                        classList={{
                          "bg-slate-500/15 text-slate-700 dark:text-slate-300": turn.local,
                          "bg-sky-500/15 text-sky-700 dark:text-sky-300": !turn.local,
                        }}
                      >
                        <TbUser class="size-2.5" aria-hidden="true" />
                        {turn.local ? t("shared_live_session.speaker_host") : t("live_sessions.speaker_remote")}
                      </span>
                      <span class="font-mono text-[10px] text-muted-text">{turn.time}</span>
                    </span>
                    <span class="block leading-relaxed text-primary-text">{turn.text}</span>
                  </span>
                </li>
              )}
            </For>
          </ul>
        </section>
        <section class="flex min-w-0 flex-1 flex-col bg-canvas">
          <div class="flex h-8 shrink-0 items-stretch border-b border-primary-border bg-panel">
            <span class="-mb-px border-b-2 border-accent bg-canvas px-3 text-xs leading-8 font-medium text-primary-text">
              {t("live_sessions.tab_live_notes")}
            </span>
          </div>
          <div class="min-h-0 flex-1">
            <LiveSummaryView items={props.data.shared.notes} />
          </div>
        </section>
      </div>
    </div>
  );
};
